import { AlertCircle } from 'lucide-react';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  children: ReactNode;
  variant?: 'error' | 'warning' | 'info' | 'success';
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ title, children, variant = 'error', className, ...props }, ref) => {
    const variants = {
      error: 'border-destructive/30 bg-destructive/10 text-destructive',
      warning: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400',
      info: 'border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-400',
      success: 'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'error-animate flex items-start gap-3 rounded-lg border p-4 text-sm shadow-sm',
          variants[variant],
          className
        )}
        role="alert"
        aria-live="polite"
        {...props}
      >
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="flex-1">
          {title && <div className="font-semibold">{title}</div>}
          <div className={title ? 'mt-1.5 opacity-90' : ''}>{children}</div>
        </div>
      </div>
    );
  }
);

Alert.displayName = 'Alert';
